import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Inbox as InboxIcon,
  Send,
  FileText,
  Clock,
  Search,
  Eye,
  MousePointerClick,
  Mailbox,
  AlertTriangle,
  Snowflake,
} from "lucide-react";
import { format } from "date-fns";
import DOMPurify from "dompurify";
import { cn } from "@/lib/utils";
import { useWorkspace } from "@/hooks/use-workspace";
import type {
  InboxThreadSummary,
  ColdThreadItem,
  ColdSentItem,
  ColdOutboxItem,
  ColdDraftItem,
  UserProfile,
} from "@shared/schema";

type ColdFolder = "inbox" | "outbox" | "sent" | "drafts";

const COLD_FOLDERS: Array<{ key: ColdFolder; label: string; icon: typeof InboxIcon }> = [
  { key: "inbox", label: "Inbox", icon: InboxIcon },
  { key: "outbox", label: "Outbox", icon: Clock },
  { key: "sent", label: "Sent", icon: Send },
  { key: "drafts", label: "Drafts", icon: FileText },
];

function formatDate(d: string | null | Date | undefined): string {
  if (!d) return "";
  try {
    return format(new Date(d), "MMM d, h:mm a");
  } catch {
    return "";
  }
}

function stripAngle(addr: string | null | undefined): string {
  if (!addr) return "";
  const m = addr.match(/^([^<]+)<([^>]+)>$/);
  if (m) return m[1].trim().replace(/^["']|["']$/g, "") || m[2].trim();
  return addr;
}

function ColdEmailNotSetup({ isAdmin }: { isAdmin: boolean }) {
  const { buildPath } = useWorkspace();
  return (
    <div className="h-full flex items-center justify-center" data-testid="cold-email-not-setup">
      <div className="max-w-md text-center space-y-3 px-6">
        <div className="mx-auto h-12 w-12 rounded-full bg-muted flex items-center justify-center">
          <Snowflake className="h-6 w-6 text-muted-foreground" />
        </div>
        <h2 className="text-lg font-semibold">Cold email isn't configured</h2>
        {isAdmin ? (
          <>
            <p className="text-sm text-muted-foreground">
              Set up a cold sending domain and relay to start using the cold outbox, inbox, and campaign tools.
            </p>
            <Link href={buildPath("/admin?section=emailSettings&tab=cold")}>
              <Button data-testid="button-cold-setup-cta">
                Go to Email Settings → Cold Email
              </Button>
            </Link>
          </>
        ) : (
          <p className="text-sm text-muted-foreground">
            Cold email hasn't been configured for this workspace. Contact your workspace admin to set it up.
          </p>
        )}
      </div>
    </div>
  );
}

export function ColdFolderView() {
  const [folder, setFolder] = useState<ColdFolder>("inbox");
  const [search, setSearch] = useState("");

  const { data: coldStatus, isLoading: coldStatusLoading } = useQuery<{ configured: boolean }>({
    queryKey: ["/api/cold/status"],
  });
  const { data: profile } = useQuery<UserProfile>({ queryKey: ["/api/profile"] });
  const isAdmin = profile?.role === "admin" || profile?.role === "super_admin";

  if (coldStatusLoading) {
    return (
      <div className="h-full flex items-center justify-center text-muted-foreground">
        <Snowflake className="h-5 w-5 animate-pulse" />
      </div>
    );
  }

  if (!coldStatus?.configured) {
    return <ColdEmailNotSetup isAdmin={isAdmin} />;
  }

  return (
    <div className="flex flex-col h-full gap-3" data-testid="component-cold-folder-view">
      <div className="flex items-center gap-1 border-b" data-testid="cold-folder-tabs">
        {COLD_FOLDERS.map(({ key, label, icon: Icon }) => {
          const active = folder === key;
          return (
            <button
              key={key}
              onClick={() => setFolder(key)}
              data-testid={`cold-rail-${key}`}
              className={cn(
                "flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors border-b-2 -mb-px",
                active
                  ? "border-primary text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground hover:border-border",
              )}
            >
              <Icon className="w-4 h-4 shrink-0" />
              <span>{label}</span>
            </button>
          );
        })}
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={`Search cold ${folder}…`}
          className="pl-8 h-9"
          data-testid="input-cold-search"
        />
      </div>

      <div className="flex-1 min-h-0 overflow-hidden">
        {folder === "inbox" && <ColdInbox search={search} />}
        {folder === "outbox" && <ColdOutbox search={search} />}
        {folder === "sent" && <ColdSent search={search} />}
        {folder === "drafts" && <ColdDrafts search={search} />}
      </div>
    </div>
  );
}

function EmptyState({ label }: { label: string }) {
  return (
    <div className="h-full flex items-center justify-center text-sm text-muted-foreground" data-testid="cold-empty-state">
      {label}
    </div>
  );
}

function ListSkeleton() {
  return (
    <div className="p-4 space-y-3">
      {[...Array(6)].map((_, i) => (
        <Skeleton key={i} className="h-14 w-full" />
      ))}
    </div>
  );
}

// ── Cold Inbox (threaded inbound replies) ────────────────────────────────
function ColdInbox({ search }: { search: string }) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const listQuery = useQuery<InboxThreadSummary[]>({
    queryKey: ["/api/cold/inbox", { search }],
    retry: false,
  });
  const rows = listQuery.data || [];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-4 h-full">
      <Card className="flex flex-col overflow-hidden" data-testid="cold-list-inbox">
        <div className="px-4 py-3 border-b flex items-center gap-2">
          <InboxIcon className="w-4 h-4" />
          <span className="font-semibold">Inbox</span>
          {!listQuery.isLoading && <span className="text-xs text-muted-foreground ml-auto">{rows.length}</span>}
        </div>
        <ScrollArea className="flex-1">
          {listQuery.isLoading ? (
            <ListSkeleton />
          ) : rows.length === 0 ? (
            <div className="p-6 text-sm text-muted-foreground text-center">No cold replies yet.</div>
          ) : (
            <div className="divide-y">
              {rows.map((t) => (
                <button
                  key={t.latestMessageId}
                  data-testid={`cold-inbox-item-${t.latestMessageId}`}
                  onClick={() => setSelectedId(t.latestMessageId)}
                  className={cn(
                    "w-full text-left px-4 py-3 hover-elevate active-elevate-2",
                    selectedId === t.latestMessageId ? "bg-muted/60" : "",
                  )}
                >
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-xs text-muted-foreground truncate flex-1">
                      {stripAngle(t.lastFromName || t.lastFromEmail)}
                    </span>
                    <span className="text-[11px] text-muted-foreground shrink-0">{formatDate(t.lastReceivedAt)}</span>
                  </div>
                  <div className="text-sm font-medium truncate flex items-center gap-1.5">
                    {t.unreadCount > 0 && <span className="w-2 h-2 rounded-full bg-primary shrink-0" />}
                    {t.subject || "(no subject)"}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">{t.preview || ""}</div>
                </button>
              ))}
            </div>
          )}
        </ScrollArea>
      </Card>

      <Card className="flex flex-col overflow-hidden" data-testid="cold-detail-inbox">
        {!selectedId ? (
          <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">
            Select a reply to view the conversation
          </div>
        ) : (
          <ColdThreadDetail id={selectedId} />
        )}
      </Card>
    </div>
  );
}

function ColdThreadDetail({ id }: { id: string }) {
  const detailQuery = useQuery<{ message: any; thread: ColdThreadItem[] }>({
    queryKey: ["/api/cold/inbox", id],
    enabled: !!id,
    retry: false,
  });

  if (detailQuery.isLoading) {
    return (
      <div className="p-6 space-y-3">
        <Skeleton className="h-6 w-2/3" />
        <Skeleton className="h-4 w-1/2" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  if (detailQuery.error) {
    return <div className="p-6 text-sm text-destructive">Failed to load conversation.</div>;
  }
  const data = detailQuery.data;
  if (!data) return null;
  const thread = data.thread.length ? data.thread : [
    {
      type: "inbound" as const,
      id: data.message.id,
      from: data.message.fromEmail,
      to: data.message.toEmails ?? null,
      cc: data.message.ccEmails ?? null,
      subject: data.message.subject,
      bodyHtml: data.message.bodyHtml ?? null,
      bodyPlainText: data.message.bodyPlainText ?? null,
      sentAt: data.message.receivedAt
        ? new Date(data.message.receivedAt).toISOString()
        : data.message.processedAt
          ? new Date(data.message.processedAt).toISOString()
          : null,
    },
  ];

  return (
    <>
      <div className="px-6 py-4 border-b">
        <div className="text-lg font-semibold" data-testid="text-cold-subject">
          {data.message.subject || "(no subject)"}
        </div>
      </div>
      <ScrollArea className="flex-1">
        <div className="p-6 space-y-6">
          {thread.map((m, idx) => (
            <div key={`${m.type}-${m.id}-${idx}`} className="space-y-2" data-testid={`cold-thread-row-${m.id}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="text-xs text-muted-foreground">
                  <Badge variant={m.type === "inbound" ? "secondary" : "outline"} className="mr-2">
                    {m.type === "inbound" ? "Received" : "Sent"}
                  </Badge>
                  {m.type === "inbound"
                    ? `From: ${stripAngle(m.from)}`
                    : `To: ${(m.to || []).map(stripAngle).join(", ")}`}
                </div>
                <span className="text-[11px] text-muted-foreground shrink-0">{formatDate(m.sentAt)}</span>
              </div>
              <div className="rounded-md border p-4 bg-card">
                {m.bodyHtml ? (
                  <div
                    className="prose prose-sm dark:prose-invert max-w-none"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(m.bodyHtml, {
                        USE_PROFILES: { html: true },
                        FORBID_TAGS: ["style", "iframe", "object", "embed", "base", "meta", "form"],
                      }),
                    }}
                  />
                ) : (
                  <pre className="whitespace-pre-wrap text-sm font-sans">{m.bodyPlainText || ""}</pre>
                )}
              </div>
            </div>
          ))}
        </div>
      </ScrollArea>
    </>
  );
}

// ── Cold Sent (delivered cold sends + open/click) ────────────────────────
function ColdSent({ search }: { search: string }) {
  const listQuery = useQuery<ColdSentItem[]>({
    queryKey: ["/api/cold/sent", { search }],
    retry: false,
  });
  const rows = listQuery.data || [];

  if (listQuery.isLoading) {
    return (
      <Card className="h-full overflow-hidden">
        <ListSkeleton />
      </Card>
    );
  }
  if (rows.length === 0) return <Card className="h-full"><EmptyState label="No cold sends yet." /></Card>;

  return (
    <Card className="h-full flex flex-col overflow-hidden" data-testid="cold-list-sent">
      <ScrollArea className="flex-1">
        <div className="divide-y">
          {rows.map((r) => (
            <div key={r.id} className="px-4 py-3" data-testid={`cold-sent-item-${r.id}`}>
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-sm font-medium truncate flex-1" data-testid={`text-cold-sent-to-${r.id}`}>
                  {r.recipientEmail}
                </span>
                <span className="text-[11px] text-muted-foreground shrink-0">{formatDate(r.sentAt)}</span>
              </div>
              <div className="text-sm truncate">{r.subject || "(no subject)"}</div>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                {r.campaignName && (
                  <Badge variant="outline" className="text-[11px]">{r.campaignName}</Badge>
                )}
                {r.mailbox && (
                  <span className="text-[11px] text-muted-foreground inline-flex items-center gap-1">
                    <Mailbox className="w-3 h-3" /> {r.mailbox}
                  </span>
                )}
                {r.opened && (
                  <span className="text-[11px] text-emerald-600 dark:text-emerald-400 inline-flex items-center gap-1" data-testid={`badge-opened-${r.id}`}>
                    <Eye className="w-3 h-3" /> Opened
                  </span>
                )}
                {r.clicked && (
                  <span className="text-[11px] text-blue-600 dark:text-blue-400 inline-flex items-center gap-1" data-testid={`badge-clicked-${r.id}`}>
                    <MousePointerClick className="w-3 h-3" /> Clicked
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </ScrollArea>
    </Card>
  );
}

// ── Cold Outbox (queued / sending / failed recipients) ───────────────────
function ColdOutbox({ search }: { search: string }) {
  const listQuery = useQuery<ColdOutboxItem[]>({
    queryKey: ["/api/cold/outbox", { search }],
    retry: false,
  });
  const rows = listQuery.data || [];

  if (listQuery.isLoading) {
    return (
      <Card className="h-full overflow-hidden">
        <ListSkeleton />
      </Card>
    );
  }
  if (rows.length === 0) return <Card className="h-full"><EmptyState label="Nothing queued or failed." /></Card>;

  const statusVariant = (s: string) =>
    s === "FAILED" ? "destructive" : s === "SENDING" ? "default" : "secondary";

  return (
    <Card className="h-full flex flex-col overflow-hidden" data-testid="cold-list-outbox">
      <ScrollArea className="flex-1">
        <div className="divide-y">
          {rows.map((r) => (
            <div key={r.id} className="px-4 py-3" data-testid={`cold-outbox-item-${r.id}`}>
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-sm font-medium truncate flex-1">{r.recipientName || r.email}</span>
                <Badge variant={statusVariant(r.status) as any} className="text-[11px] shrink-0">{r.status}</Badge>
              </div>
              <div className="text-sm truncate">{r.subject || "(no subject)"}</div>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                {r.campaignName && <Badge variant="outline" className="text-[11px]">{r.campaignName}</Badge>}
                {r.mailbox && (
                  <span className="text-[11px] text-muted-foreground inline-flex items-center gap-1">
                    <Mailbox className="w-3 h-3" /> {r.mailbox}
                  </span>
                )}
                {r.nextSendAt && r.status !== "FAILED" && (
                  <span className="text-[11px] text-muted-foreground inline-flex items-center gap-1">
                    <Clock className="w-3 h-3" /> {formatDate(r.nextSendAt)}
                  </span>
                )}
                {r.failedReason && (
                  <span className="text-[11px] text-destructive inline-flex items-center gap-1" data-testid={`text-cold-outbox-error-${r.id}`}>
                    <AlertTriangle className="w-3 h-3" /> {r.failedReason}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </ScrollArea>
    </Card>
  );
}

// ── Cold Drafts (cold campaigns still in DRAFT) ──────────────────────────
function ColdDrafts({ search }: { search: string }) {
  const listQuery = useQuery<ColdDraftItem[]>({
    queryKey: ["/api/cold/drafts", { search }],
    retry: false,
  });
  const rows = listQuery.data || [];

  if (listQuery.isLoading) {
    return (
      <Card className="h-full overflow-hidden">
        <ListSkeleton />
      </Card>
    );
  }
  if (rows.length === 0) return <Card className="h-full"><EmptyState label="No cold drafts." /></Card>;

  return (
    <Card className="h-full flex flex-col overflow-hidden" data-testid="cold-list-drafts">
      <ScrollArea className="flex-1">
        <div className="divide-y">
          {rows.map((r) => (
            <div key={r.id} className="px-4 py-3" data-testid={`cold-draft-item-${r.id}`}>
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-sm font-medium truncate flex-1">{r.campaignName}</span>
                <span className="text-[11px] text-muted-foreground shrink-0">{formatDate(r.createdAt)}</span>
              </div>
              <div className="text-sm truncate">{r.subject || "(no subject)"}</div>
              <div className="text-[11px] text-muted-foreground mt-1">
                {r.totalRecipients} recipient{r.totalRecipients === 1 ? "" : "s"}
              </div>
            </div>
          ))}
        </div>
      </ScrollArea>
    </Card>
  );
}
